import LoadingStore from '../stores/LoadingStore';
import {reload, navigateTo, parseQuerystring} from './URL';
import {readCookie} from 'utilities/cookie';
import {EventEmitter} from 'events';
import axios from 'axios';

const UNAUTHORIZED = 401;

axios.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8';

const ajax = opts => {
  const source = axios.CancelToken.source();
  const request = axios({...opts, cancelToken: source.token});
  const wrapped = request.then(res => res.data).catch(err => {
    const error = Object.assign(
      new Error('Request Failed'),
      err,
      axios.isCancel(err) ? {statusText: 'abort'} : null
    );
    return Promise.reject(error);
  });
  wrapped.cancel = () => source.cancel();

  return wrapped;
};

class Request extends EventEmitter {
  cache = {};
  headers = {};
  reloadOnUnauthorized = false;

  buildHeaders = configHeaders => ({
    'x-csrf-token': readCookie('csrf-token'),
    'Accept-Language': global.ENV.locale,
    ...this.headers,
    ...configHeaders,
  });

  addHeader(name, value) {
    this.headers[name] = value;
  }

  subscribe(request) {
    const onFinish = () => this.emit('finish', request);
    const onFail = err => this.emit('fail', request, err);
    this.emit('start', request);
    request.then(null, onFail);
    request.then(onFinish, onFinish);
  }

  get(url, config = {}) {
    if (this.cache[url]) return this.cache[url];

    const request = ajax({
      method: 'GET',
      url,
      ...config,
      headers: this.buildHeaders(config.headers),
    });

    const oldCancel = request.cancel;
    request.cancel = () => {
      delete this.cache[url];
      oldCancel.apply(request);
    };
    const removeFromCache = () => delete this.cache[url];
    request.then(removeFromCache, removeFromCache);
    this.subscribe(request);
    this.cache[url] = request;

    return request;
  }

  post(url, data, config = {}) {
    const body = {
      method: 'POST',
      url,
      data,
      ...config,
      headers: this.buildHeaders(config.headers),
    };

    if (data instanceof File) {
      const upload = new FormData();
      upload.append('file', data);
      body.data = upload;
    }

    const request = ajax(body);
    this.subscribe(request);
    return request;
  }

  put(url, data, config = {}) {
    const body = {
      method: 'PUT',
      url,
      data,
      ...config,
      headers: this.buildHeaders(config.headers),
    };

    const request = ajax(body);
    this.subscribe(request);
    return request;
  }

  delete(url, config = {}) {
    const request = ajax({
      method: 'DELETE',
      url,
      ...config,
      headers: this.buildHeaders(config.headers),
    });

    this.subscribe(request);
    return request;
  }
}

const instance = new Request();

instance.on('start', () => {
  LoadingStore.start();
});

instance.on('finish', () => {
  LoadingStore.finish();
});

instance.on('fail', (request, err) => {
  const status = err.response && err.response.status;
  if (status === UNAUTHORIZED && instance.reloadOnUnauthorized) {
    if (process.env.NODE_ENV === 'production') {
      reload();
    } else {
      // Prevent redirect loop in development
      navigateTo('/');
    }
  }
});

export default instance;

export const cancelRequest = request =>
  request && request.cancel && request.cancel();

export const init = ({useDeveloperToken, reloadOnUnauthorized}) => {
  const query = parseQuerystring();
  if (useDeveloperToken && query.token) {
    instance.addHeader('X-DOMO-Developer-Token', query.token);
  }
  if (global.ENV.auth) {
    instance.addHeader('X-DOMO-Embed-Token', global.ENV.auth);
  }

  instance.reloadOnUnauthorized = !!reloadOnUnauthorized;
};
